import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NoteAttachmentView } from '../note-attachment.model';

/**
 * The attachment list for an internal note, a reply, or a note composer — on the
 * NEW (Pebbl / console-v2) note design only.
 *
 * The chip is the reference design's own: a rounded `--surface-2` pill holding a
 * 13px paperclip, the filename, a preview eye and a remove ✕, all at the values
 * `.note-file-row` carried on the six surfaces that already ship this design.
 * Rendering N of them in a wrapping row is the whole visual change that
 * multi-attachment needs; the chip itself is untouched.
 *
 * There is deliberately no second chip style and no per-file-type variant: every
 * attachment, of every type, on every NEW-design surface, is this chip.
 *
 * Equally deliberately, the OLD design does not come through here. The legacy
 * surfaces — `admin-core/components/internal-notes` and the five detail pages
 * that embed it, `shared/modal/add-update-note-modal`,
 * `shared/modal/follow-up-internal-note-modal`, and the pebbl-feedback compose
 * bar — keep their own markup and their own stylesheets, each repeated per file.
 * They render three different legacy looks (a bordered `<li>` with 30px icon
 * buttons, a `.file-attach` row with a FontAwesome eye, and the `.fd-attached-file`
 * pill); folding those into this component would have meant either a third and
 * fourth skin here or a redesign of pages that are out of scope. Nothing in this
 * file is loaded on those pages, so this component cannot move them.
 *
 * Purely presentational: it emits an intent per row (`preview` / `download` /
 * `delete`) carrying the row it came from, and makes no HTTP call of its own —
 * each host already owns its confirm dialog, toast copy and reload behaviour.
 */
@Component({
  selector: 'app-note-attachment-list',
  standalone: true,
  imports: [CommonModule],
  // Eager, matching internal-notes and the rest of this console: Angular 22
  // defaults to OnPush, and hosts mutate these rows in place (splicing their
  // array inside a subscribe callback) rather than replacing them.
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './note-attachment-list.component.html',
  styleUrl: './note-attachment-list.component.css',
})
export class NoteAttachmentListComponent {
  @Input() attachments: NoteAttachmentView[] = [];

  /** Host decides — `internal-notes` already has `canDeleteAttachment(note)`. */
  @Input() canDelete = false;

  /**
   * Whether STORED rows offer Preview. An unsent pick never does, whatever this
   * says — see `canPreview`.
   */
  @Input() showPreview = true;

  /**
   * Turns the filename into the download control, which is how every surface has
   * always exposed download — the reference chip has no download button. Off for
   * unsaved picks, which have nothing on the server to fetch.
   */
  @Input() showDownload = false;

  /**
   * `Delete file` on a saved attachment, `Remove file` on an unsaved pick: one
   * destroys stored data and the other only drops a selection, and the confirm
   * dialog only appears on the first.
   */
  @Input() deleteTip = 'Delete file';

  /**
   * The tip (and aria-label) for a row that is only a LOCAL pick, when that
   * differs from `deleteTip`. Defaults to `deleteTip`, so a list of one kind of
   * row needs nothing extra.
   *
   * It exists for the composer, which shows both kinds in one row while editing:
   * a stored file's ✕ destroys data behind a confirm dialog, an unsent pick's
   * only drops a selection. Naming both "Delete file" told the user the second
   * one was destructive; naming both "Remove file" hid that the first is.
   */
  @Input() localDeleteTip = '';

  @Output() preview = new EventEmitter<NoteAttachmentView>();
  @Output() download = new EventEmitter<NoteAttachmentView>();
  @Output() delete = new EventEmitter<NoteAttachmentView>();

  /**
   * Preview is for STORED attachments only.
   *
   * A row with `local` set has no server presence at all — no attachment id, so
   * nothing for `note/preview` to resolve — and offering an eye on it promises a
   * capability the persisted flow does not have there yet. The Add/Edit Note
   * dropzone has always said this by passing `showPreview: false` for its picks;
   * stating it on the ROW instead means no host has to remember the flag, and a
   * file cannot flash an eye between being picked and being saved, because the
   * answer never depended on timing.
   *
   * `showPreview` still governs stored rows — that is where a host says whether
   * the record exists yet (`!!noteModel?.id` in the dialogs).
   */
  canPreview(row: NoteAttachmentView): boolean {
    return this.showPreview && !row?.local;
  }

  /** `localDeleteTip` for an unsent pick, `deleteTip` for anything stored. */
  tipFor(row: NoteAttachmentView): string {
    return row?.local && this.localDeleteTip ? this.localDeleteTip : this.deleteTip;
  }

  /**
   * Row identity. Prefers the attachment id; a legacy mirror row and a local
   * draft file have none, so those fall back to name+index, which is stable for
   * as long as the row exists. Without this, deleting the 2nd of 5 rows
   * re-creates the other four — and with them their focus and hover state.
   */
  trackRow = (index: number, row: NoteAttachmentView): string =>
    row.id != null ? `id:${row.id}` : `n:${row.name}:${index}`;

  get ariaLabel(): string {
    const n = this.attachments.length;
    return `${n} attachment${n === 1 ? '' : 's'}`;
  }

  /**
   * The filename split for MIDDLE truncation, so the extension survives a narrow
   * chip.
   *
   * The tail is the part that must survive: it carries the extension, and a plain
   * end-ellipsis would eat exactly that ("GatherCo_Pebbl_Product_Knowl…" tells you
   * nothing about the file type). The head takes the ellipsis; the tail is never
   * shrunk. Short names — every name in the reference screenshot — return an
   * empty tail and render as a single run, byte-identical to before.
   */
  nameHead(row: NoteAttachmentView): string {
    const name = row?.name ?? '';
    return name.length > NoteAttachmentListComponent.TRUNCATE_OVER
      ? name.slice(0, -NoteAttachmentListComponent.TAIL_CHARS)
      : name;
  }

  nameTail(row: NoteAttachmentView): string {
    const name = row?.name ?? '';
    return name.length > NoteAttachmentListComponent.TRUNCATE_OVER
      ? name.slice(-NoteAttachmentListComponent.TAIL_CHARS)
      : '';
  }

  /** Below this length the name fits the chip at every breakpoint — leave it whole. */
  private static readonly TRUNCATE_OVER = 22;
  /** Enough to clear the longest extension in use (.jpeg/.docx) and still read. */
  private static readonly TAIL_CHARS = 10;

  onPreview(row: NoteAttachmentView): void {
    this.preview.emit(row);
  }

  onDownload(row: NoteAttachmentView): void {
    this.download.emit(row);
  }

  onDelete(row: NoteAttachmentView): void {
    this.delete.emit(row);
  }
}
